// @givmo/passkeys — the step-up sheet's passkey arm, as a host fills it (AS-WEB10; D-AS-17).
//
// `@givmo/step-up` draws the arm and owns its states; a host fills the slot with the ACCOUNT-BOUND
// re-confirmation, which only an app's auth context can run in full (`@givmo/auth`'s
// `reconfirmWithPasskey`, then the suite session login re-run with the property's remember-me
// choice and sign-out cancel handle). This file turns that host action into the slot and its typed
// rejections, so every host reads a failure the same way.
//
// ⚠ NEVER the unbound passkey sign-in. `signInWithPasskey` / `adminSignInWithPasskey` sign in
// whichever account asserts; a slot holding either would let another account finish the act the
// sheet is gating. `@givmo/auth`'s `index.test.ts` fails any module that fills a slot and names
// either — this one included.

import { AuthError } from '@givmo/auth';
import { StepUpPasskeyFailure, type StepUpPasskeySlot } from '@givmo/step-up';

export interface PasskeyStepUpSlotOptions {
  /** Whether the account holds a passkey (`GET /users/me/passkeys`). */
  holdsPasskey: boolean;
  /**
   * The host's account-bound re-confirmation: `reconfirmWithPasskey`, then the suite session
   * login re-run. Rejects with `@givmo/auth`'s `AuthError`.
   */
  reconfirm: () => Promise<void>;
  /** Where "Lost your passkey?" leads, once a host has the web reset (a later row). */
  lost?: () => void;
}

/**
 * The typed rejection a re-confirmation failure becomes on the sheet, or the failure itself when it
 * is neither: the account holds no passkey (`none_registered`, the sheet moves on to the person's
 * own way in); a dismissal, a failed ceremony or a verification the authenticator did not perform
 * (`cancelled_or_failed`, the sheet stays and says so). Anything else (another account, the
 * company's SSO lock, a rate limit, the network) keeps its own words.
 */
export function passkeyStepUpFailure(thrown: unknown): unknown {
  if (!(thrown instanceof AuthError)) return thrown;
  switch (thrown.kind) {
    case 'passkeyNoneRegistered':
      return new StepUpPasskeyFailure('none_registered');
    case 'cancelled':
    case 'passkeyCeremonyFailed':
    case 'passkeyUserVerificationRequired':
      return new StepUpPasskeyFailure('cancelled_or_failed');
    default:
      return thrown;
  }
}

/** The slot a host hands `@givmo/step-up` (`StepUpHostAdapter.passkey`). */
export function passkeyStepUpSlot({
  holdsPasskey,
  reconfirm,
  lost,
}: PasskeyStepUpSlotOptions): StepUpPasskeySlot {
  return {
    holdsPasskey,
    confirm: async () => {
      try {
        await reconfirm();
      } catch (thrown) {
        throw passkeyStepUpFailure(thrown);
      }
    },
    ...(lost !== undefined ? { lost } : {}),
  };
}
